import { useEffect, useRef, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { authClient } from '../../lib/auth-client';
import MobileNavDrawer from './MobileNavDrawer';
import { useDueRecurringSchedules } from '@/hooks/useRecurringSchedules';

interface NavItem {
  path: string;
  label: string;
  badge?: number;
}

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: dueSchedules } = useDueRecurringSchedules();
  const dueCount = dueSchedules?.length ?? 0;

  const navItems: NavItem[] = [
    { path: '/jobs', label: 'Jobs' },
    { path: '/invoices', label: 'Invoices' },
    { path: '/clients', label: 'Clients' },
    { path: '/timesheets', label: 'Timesheets' },
    { path: '/recurring', label: 'Recurring', badge: dueCount > 0 ? dueCount : undefined },
    { path: '/settings', label: 'Settings' },
    { path: '/help', label: 'Help' },
  ];
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const headerRef = useRef<HTMLDivElement | null>(null);

  const handleLogout = async () => {
    await authClient.signOut();
    navigate('/login');
  };

  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  const openDrawer = () => {
    setIsDrawerOpen(true);
    setTimeout(() => setIsAnimating(true), 10);
  };

  const closeDrawer = () => {
    setIsAnimating(false);
    setTimeout(() => setIsDrawerOpen(false), 300);
  };

  const handleNavClick = () => {
    closeDrawer();
  };

  useEffect(() => {
    const updateHeaderHeight = () => {
      if (headerRef.current) {
        document.documentElement.style.setProperty(
          '--app-header-height',
          `${headerRef.current.offsetHeight}px`
        );
      }
    };

    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight);

    return () => {
      window.removeEventListener('resize', updateHeaderHeight);
    };
  }, []);

  return (
    <div ref={headerRef} className="sticky top-0 z-50 bg-white shadow">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        {/* Tighter on a phone: this bar is sticky and the job detail page sizes
            itself off --app-header-height, so every pixel here is one the tab
            content below doesn't get. */}
        <div className="flex justify-between items-center py-2 sm:py-4">
          <div className="flex items-center gap-4 sm:gap-8">
            {/* Hamburger Menu - Mobile Only */}
            <button
              onClick={openDrawer}
              className="md:hidden p-2 text-gray-600 hover:text-gray-900 focus:outline-none"
              aria-label="Open menu"
            >
              <Menu className="h-6 w-6" />
            </button>

            <Link to="/jobs">
              <h1 className="text-xl sm:text-3xl font-bold text-gray-900 cursor-pointer hover:text-gray-700 transition-colors">
                JobOS
              </h1>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex gap-6">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`text-sm font-medium transition-colors flex items-center gap-1.5 ${
                    isActive(item.path)
                      ? 'text-blue-600 border-b-2 border-blue-600 pb-1'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {item.label}
                  {item.badge !== undefined && (
                    <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-semibold">
                      {item.badge}
                    </span>
                  )}
                </Link>
              ))}
            </nav>
          </div>
          <div className="hidden md:flex items-center gap-4">
            <button
              onClick={handleLogout}
              className="btn btn-secondary"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Drawer */}
      <MobileNavDrawer
        isOpen={isDrawerOpen}
        isAnimating={isAnimating}
        onClose={closeDrawer}
        onNavClick={handleNavClick}
        isActive={isActive}
        navItems={navItems}
        onLogout={handleLogout}
      />
    </div>
  );
}
